import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { QUOTE_FORM } from "@/content/copy";

const INDUSTRIES = ["Trade / construction", "Tech startup", "Professional services", "Other service business", "Other"];
const XERO_STATUS = ["On Xero - it's a mess", "On Xero - mostly OK", "On MYOB or other software", "Spreadsheets / shoebox", "Not set up yet"];
const VOLUMES = ["Under 100", "100-300", "300-1,000", "1,000+", "Not sure"];
const CONTACT_PREFS = ["Email", "Phone call", "Either"];

const inputCls =
  "w-full rounded-xl border-2 border-navy/15 bg-white px-4 py-3 text-base text-navy placeholder:text-navy/40 focus:border-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta";

function Field({ label, htmlFor, required, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-bold text-navy mb-1.5">
        {label}{required && <span className="text-terracotta"> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function QuoteForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", business: "", industry: "", xero_status: "",
    monthly_volume: "", message: "", preferred_contact: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/leads/quote", form);
      trackEvent("quote_form_submit", { industry: form.industry });
      navigate("/thank-you?source=quote");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <section data-testid="quote-form-section" className="py-20 lg:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-bold uppercase tracking-widest text-terracotta">{QUOTE_FORM.overline}</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-navy">{QUOTE_FORM.title}</h2>
        <p className="mt-4 text-base text-navy/70 leading-relaxed">{QUOTE_FORM.subtitle}</p>

        <form data-testid="quote-form" onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Your name" htmlFor="q-name" required>
            <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} autoComplete="name" className={inputCls} placeholder="Jane Citizen" />
          </Field>
          <Field label="Email" htmlFor="q-email" required>
            <input id="q-email" data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} autoComplete="email" className={inputCls} placeholder="you@business.com.au" />
          </Field>
          <Field label="Business name" htmlFor="q-business">
            <input id="q-business" data-testid="quote-business-input" value={form.business} onChange={set("business")} autoComplete="organization" className={inputCls} placeholder="Citizen Plumbing Pty Ltd" />
          </Field>
          <Field label="Industry" htmlFor="q-industry">
            <select id="q-industry" data-testid="quote-industry-select" value={form.industry} onChange={set("industry")} className={inputCls}>
              <option value="">Select…</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Where is your bookkeeping at?" htmlFor="q-xero">
            <select id="q-xero" data-testid="quote-xero-status-select" value={form.xero_status} onChange={set("xero_status")} className={inputCls}>
              <option value="">Select…</option>
              {XERO_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Rough monthly transactions" htmlFor="q-volume">
            <select id="q-volume" data-testid="quote-volume-select" value={form.monthly_volume} onChange={set("monthly_volume")} className={inputCls}>
              <option value="">Select…</option>
              {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Preferred contact" htmlFor="q-contact">
              <select id="q-contact" data-testid="quote-contact-select" value={form.preferred_contact} onChange={set("preferred_contact")} className={inputCls}>
                <option value="">Select…</option>
                {CONTACT_PREFS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="What's going on with your file?" htmlFor="q-message">
              <textarea id="q-message" data-testid="quote-message-input" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="e.g. We're 8 months behind on reconciliations and BAS is due…" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="quote-submit-button"
              disabled={loading}
              className="w-full sm:w-auto rounded-full bg-terracotta px-10 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              {loading ? "Sending…" : "Request a quote"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
